// @core/stores/userprofile/UserprofileAvatarStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { userprofileApi } from '@core/api';
import type {
  ActivateAvatarRequest,
  Avatar,
  CreateAvatarsBulkRequest,
  CreateAvatarsBulkResponse,
  DeleteAvatarsBulkResponse,
  UpdateAvatarRequest,
} from '@core/types/userprofile';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class UserprofileAvatarStore {
  // List state
  items: Avatar[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Avatar | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Avatar>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Avatar | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Avatar | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Set avatar as active
   */
  async ActivateAvatar(profile_id: string, avatar_id: string, data: ActivateAvatarRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.ActivateAvatar(profile_id, avatar_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new avatar
   */
  async CreateAvatar(file: File): Promise<Avatar> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userprofileApi.CreateAvatar(file);
      const item = response.avatar;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create multiple avatars
   */
  async CreateAvatarsBulk(data: CreateAvatarsBulkRequest): Promise<CreateAvatarsBulkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.CreateAvatarsBulk(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete avatar
   */
  async DeleteAvatar(profile_id: string, avatar_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await userprofileApi.DeleteAvatar(profile_id, avatar_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== avatar_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === avatar_id) {
          this.currentItem = null;
        }

        this.removeFromCache(avatar_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete multiple avatars
   */
  async DeleteAvatarsBulk(): Promise<DeleteAvatarsBulkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.DeleteAvatarsBulk();

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get avatar by ID
   */
  async GetAvatar(profile_id: string, avatar_id: string): Promise<Avatar> {
    // Check cache first
    const cached = this.cache.get(avatar_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await userprofileApi.GetAvatar(profile_id, avatar_id);
      const item = response.avatar;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List avatars for profile
   */
  async ListAvatars(profile_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userprofileApi.ListAvatars(profile_id, params);

      runInAction(() => {
        this.items = response.avatars?.avatars ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List avatars by owner
   */
  async ListAvatarsByOwner(owner_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userprofileApi.ListAvatarsByOwner(owner_id, params);

      runInAction(() => {
        this.items = response.avatars?.avatars ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update avatar
   */
  async UpdateAvatar(profile_id: string, avatar_id: string, data: UpdateAvatarRequest): Promise<Avatar> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userprofileApi.UpdateAvatar(profile_id, avatar_id, data);
      const item = response.avatar;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const userprofileAvatarStore = new UserprofileAvatarStore();
